import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Form } from 'src/shared/components/ui/forms/form'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { ProfessionEnum } from 'src/shared/features/users/schemas/profession'
import type { LoanPurpose } from 'src/shared/schemas/referrals'
import type { ClientType } from 'src/shared/schemas/referrals/referral-fields/real-estate-agent/client-type'

import BusinessFormButtonsField from '../../components/business-form-buttons-field'
import BusinessFormTitle from '../../components/business-form-title'
import { useBusinessCreateContext } from '../../context/business-create-context'
import { useBusinessCreateStore } from '../../store/business-create-store'

const PurposeSchema = z.object({
  purpose: z.string().min(1, { message: 'Purpose is required' }),
})
type PurposeSchemaType = z.infer<typeof PurposeSchema>

export default function BusinessCreatePurposeStep() {
  const { setFormData, formData } = useBusinessCreateStore()
  const { handleNext, setFooterConfig } = useBusinessCreateContext()
  const { data: user } = useMyUserData()
  const profession = user?.profession

  const options =
    profession === ProfessionEnum.MORTGAGE_LOAN_OFFICER
      ? [
          {
            label: 'Purchase',
            value: 'purchase',
          },
          {
            label: 'Refinance',
            value: 'refinance',
          },
          {
            label: 'Other',
            value: 'other',
          },
        ]
      : [
          {
            label: 'Buy',
            value: 'buy',
          },
          {
            label: 'Sell',
            value: 'sell',
          },
          {
            label: 'Lease',
            value: 'lease',
          },
        ]

  const form = useForm<PurposeSchemaType>({
    resolver: zodResolver(PurposeSchema),
    defaultValues: {
      purpose: formData.clientType ?? '',
    },
  })

  useEffect(() => {
    setFooterConfig({
      buttonText: 'Skip',
      buttonVariant: 'ghost',
      primaryAction: () => handleNext(),
    })
  }, [handleNext, setFooterConfig])

  const onSubmit = (data: PurposeSchemaType) => {
    const purpose =
      profession === ProfessionEnum.MORTGAGE_LOAN_OFFICER
        ? { loanPurpose: data.purpose as LoanPurpose }
        : { clientType: data.purpose as ClientType }
    setFormData({ ...formData, ...purpose })
    handleNext()
  }

  const onPurposePress = () => {
    form.handleSubmit(onSubmit)()
  }

  return (
    <div className="flex size-full flex-col items-center justify-center gap-10">
      <BusinessFormTitle>{`What is ${formData.consumerFirstName || 'your client'} interested in?`}</BusinessFormTitle>
      <Form {...form}>
        <form id="business-create-form" onSubmit={form.handleSubmit(onSubmit)} className="w-full">
          <BusinessFormButtonsField options={options} control={form.control} name="purpose" onPress={onPurposePress} />
        </form>
      </Form>
    </div>
  )
}
